import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { openTab, closeTab } from "../redux/actions/workspaceActions";
import { SIDEBAR_ITEMS } from "../config/sidebarConfig";

const useWorkspaceNavigation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * Helper function to match dynamic URLs to a specific component key and title.
   */
  const getDynamicTabData = (path) => {
    if (path.startsWith("/receipts/") && path.split("/").length === 3) {
      const id = path.split("/").pop();
      const shortId = id.substring(0, 8);
      return {
        title: `Receipt #${shortId}...`,
        componentKey: "RECEIPT",
      };
    } else if (path.startsWith("/invoices/") && path.split("/").length === 3) {
      const id = path.split("/").pop();
      const shortId = id.substring(0, 8);
      return {
        title: `Invoice #${shortId}...`,
        componentKey: "INVOICE",
      };
    } else if (
      path.startsWith("/fiscal/invoices/") &&
      path.split("/").length === 4
    ) {
      const id = path.split("/").pop();
      const shortId = id.substring(0, 8);
      return {
        title: `Fiscal Invoice #${shortId}...`,
        componentKey: "FISCAL_INVOICE",
      };
    } else if (
      path.startsWith("/credit-notes/") &&
      path.split("/").length === 3
    ) {
      const id = path.split("/").pop();
      const shortId = id.substring(0, 8);
      return {
        title: `C.Note #${shortId}...`,
        componentKey: "CREDIT_NOTE",
      };
    }
    return null;
  };

  /**
   * Navigates to a path and automatically opens the correct tab
   * @param {string} path - The destination URL (e.g., "/shifts" or "/receipts/123")
   */
  const navigateToTab = (path) => {
    // 1. Try to find the exact item in your sidebar config (for static routes)
    const item = SIDEBAR_ITEMS.find((i) => i.path === path);

    let tabData = null;

    if (item && item.componentKey) {
      // It's a static route (e.g., "/receipts")
      tabData = {
        id: item.path,
        title: item.title,
        path: item.path,
        componentKey: item.componentKey,
      };
    } else {
      // 2. It's not in the sidebar. Try to match it as a dynamic route.
      const dynamicData = getDynamicTabData(path);
      if (dynamicData) {
        tabData = {
          id: path, // Use the full dynamic path as the unique tab ID
          title: dynamicData.title,
          path: path,
          componentKey: dynamicData.componentKey,
        };
      }
    }

    if (tabData) {
      // 3. Dispatch action to open/activate the tab
      dispatch(openTab(tabData));
    } else {
      console.warn(`No workspace mapping found for path: ${path}`);
    }

    // 4. Perform the actual route change
    navigate(path);
  };

  /**
   * Closes the specified tab (defaults to current URL) and navigates to a fallback path.
   * @param {string} fallbackPath - Where to go after closing (e.g., "/receipts")
   * @param {string} tabIdToClose - Optional. Defaults to current path.
   */
  const closeCurrentAndNavigate = (fallbackPath, tabIdToClose = null) => {
    // The ID of the tab is its full path
    const targetId = tabIdToClose || window.location.pathname;

    // 1. Close the tab in Redux
    dispatch(closeTab(targetId));

    // 2. Navigate to the fallback list view
    // (This will also activate the fallback tab via AppWrapper sync logic)
    navigateToTab(fallbackPath);
  };

  return { navigateToTab, closeCurrentAndNavigate };
};

export default useWorkspaceNavigation;
